import { addSignal } from '../addSignal';
import { createRootReducer } from '../reducers';
import { addRunFunction } from '../runnable';
import { rootSaga } from '../sagas';
import { addSubscribeSelector } from '../subscribeSelector';
import type { SagaContext } from '../types';
import { composeWithDevTools } from '@redux-devtools/extension';
import { TypedContainer, TypedContainerModule } from '@thinkalpha/common/ioc/TypedContainer.js';
import { createBrowserHistory } from 'history';
import type { Container } from 'inversify';
import { applyMiddleware, compose, legacy_createStore as createStore } from 'redux';
import type { Middleware } from 'redux';
import createSagaMiddleware from 'redux-saga';
import type { IsomorphicBindings } from 'src/types/bindings';

export const localStoreModule = new TypedContainerModule<IsomorphicBindings>((bind) => {
    const history = APP_ELECTRON ? null : createBrowserHistory();

    bind('Store')
        .toDynamicValue((ctx) => {
            const container = ctx.container;
            const context: SagaContext = {
                configService: container.get('ConfigService'),
                electronService: container.isBound('ElectronService') ? container.get('ElectronService') : undefined,
                loginScreenService: container.get('LoginScreenService'),
                oAuth2Client: container.get('OAuth2Service'),
                launchDarklyClient: container.get('LaunchDarklyClient'),
                container: new TypedContainer<IsomorphicBindings>(container as unknown as Container),
            };

            const sagaMiddleware = createSagaMiddleware({ context });

            const middlewares: Middleware[] = [sagaMiddleware];
            const middlewareEnhancer = applyMiddleware(...middlewares);

            const enhancers = compose(
                addSignal,
                addSubscribeSelector,
                addRunFunction(() => {
                    const task = sagaMiddleware.run(rootSaga);
                    return () => task.cancel();
                }),
                composeWithDevTools({ trace: TRACE, traceLimit: 10 })(middlewareEnhancer),
            );

            // Casting here since the return type of `createStore` doesn't reflect the enhancers we're adding
            const store = createStore(createRootReducer(), {}, enhancers) as IsomorphicBindings['Store'];

            return store;
        })
        .inSingletonScope();

    if (history) bind('History').toConstantValue(history);
});
